import { API } from '@/config/api';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Undo, Redo, Minus, Sparkles, Loader2, Type, Quote } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

function ToolbarButton({ onClick, active, children, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
        disabled ? 'opacity-30 cursor-not-allowed' :
        active ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

export default function RichTextEditor({ content, onChange, placeholder }) {
  const [aiFormatting, setAiFormatting] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Placeholder.configure({ placeholder: placeholder || 'Skriv her...' }),
    ],
    content: mdToHtml(content || ''),
    onUpdate: ({ editor }) => {
      onChange(htmlToMd(editor.getHTML()));
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[350px] max-h-[50vh] overflow-y-auto px-5 py-4 text-[15px] leading-relaxed text-slate-800 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-4 [&_h3]:mb-1.5 [&_strong]:font-semibold [&_strong]:text-slate-900 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-0.5 [&_hr]:my-4 [&_hr]:border-slate-200 [&_p]:my-1.5 [&_blockquote]:border-l-3 [&_blockquote]:border-teal-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600',
      },
    },
  });

  useEffect(() => {
    if (editor && content !== undefined) {
      const cur = htmlToMd(editor.getHTML());
      if (cur !== content) {
        editor.commands.setContent(mdToHtml(content || ''));
      }
    }
  }, [content, editor]);

  const handleAiFormat = async () => {
    if (!editor || aiFormatting) return;
    const currentText = htmlToMd(editor.getHTML());
    if (!currentText.trim()) return;

    setAiFormatting(true);
    try {
      const res = await axios.post(`${API}/chat/send`, {
        session_id: `format-${Date.now()}`,
        message: `Formater følgjande tekst profesjonelt for ein laboratorierapport. Bruk overskrifter (##, ###), punktlister, og bold for viktige funn. IKKJE endre innhaldet - berre forbetre struktur og formattering. Svar KUN med den formaterte teksten, ingen ekstra kommentarar:\n\n${currentText}`
      }, { withCredentials: true });
      
      const formatted = res.data.message?.content;
      if (formatted) {
        editor.commands.setContent(mdToHtml(formatted));
        onChange(formatted);
      }
    } catch {
      // Silently fail
    } finally { setAiFormatting(false); }
  };

  if (!editor) return null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-slate-200 bg-slate-50/80">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Fet (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Kursiv (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Understrek (Ctrl+U)">
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Overskrift">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Underoverskrift">
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph') && !editor.isActive('heading')} title="Brødtekst">
          <Type className="w-4 h-4" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Punktliste">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Nummerert liste">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Sitat">
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Skillelinje">
          <Minus className="w-4 h-4" />
        </ToolbarButton>

        <div className="flex-1" />

        {/* AI Format button */}
        <button
          onClick={handleAiFormat}
          disabled={aiFormatting}
          title="AI: Forbetre formattering og struktur"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            aiFormatting
              ? 'bg-violet-100 text-violet-500'
              : 'bg-gradient-to-r from-violet-500 to-teal-500 text-white hover:from-violet-600 hover:to-teal-600 shadow-sm'
          }`}
        >
          {aiFormatting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {aiFormatting ? 'Formaterer...' : 'AI Formater'}
        </button>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Angre">
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Gjenta">
          <Redo className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-slate-100 bg-slate-50/50 text-[10px] text-slate-400">
        <span>{editor.storage.characterCount?.characters?.() || editor.getText().length} teikn</span>
        <span>Marker tekst for hurtigformattering</span>
      </div>
    </div>
  );
}

function mdToHtml(md) {
  if (!md) return '<p></p>';
  let html = md
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^- (.*$)/gm, '<li>$1</li>');
  
  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li>.*?<\/li>\n?)+/gs, (match) => `<ul>${match}</ul>`);
  
  // Split remaining text into paragraphs
  const lines = html.split('\n');
  return lines.map(line => {
    const trimmed = line.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<h') || trimmed.startsWith('<ul') || trimmed.startsWith('<ol') || trimmed.startsWith('<hr') || trimmed.startsWith('<li') || trimmed.startsWith('<blockquote')) return trimmed;
    return `<p>${trimmed}</p>`;
  }).filter(Boolean).join('');
}

function htmlToMd(html) {
  if (!html) return '';
  return html
    .replace(/<h2>(.*?)<\/h2>/g, '## $1\n')
    .replace(/<h3>(.*?)<\/h3>/g, '### $1\n')
    .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
    .replace(/<em>(.*?)<\/em>/g, '*$1*')
    .replace(/<u>(.*?)<\/u>/g, '$1')
    .replace(/<li><p>(.*?)<\/p><\/li>/g, '- $1\n')
    .replace(/<li>(.*?)<\/li>/g, '- $1\n')
    .replace(/<\/?ul>/g, '')
    .replace(/<\/?ol>/g, '')
    .replace(/<hr>/g, '---\n')
    .replace(/<blockquote><p>(.*?)<\/p><\/blockquote>/g, '> $1\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<p>(.*?)<\/p>/g, '$1\n\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
